import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import {
  Grid,
  Card,
  Icon,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Box,
  Avatar,
  Chip,
  ListItemAvatar,
  ListItemText,
  InputAdornment,
  ListSubheader,
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import PageHeader from "components/PageHeader";
import DataTable from "components/DataTable";
import ConfirmDialog from "components/ConfirmDialog";
import FormDialog from "components/FormDialog";
import FormSection from "components/FormSection";
import useUserData from "layouts/tables/data/userTableData";
import { useAuth } from "contexts/AuthContext";
import { API_BASE, API_HOST } from "config/api";

// Role catalogue — icon, label, description, gradient. Drives the rich Role select.
const ROLE_CATALOGUE = [
  {
    value: "admin",
    label: "Administrator",
    desc: "Full access to every page and setting.",
    icon: "admin_panel_settings",
    gradient: "linear-gradient(135deg,#1A237E,#3949AB)",
  },
  {
    value: "pastor",
    label: "Pastor",
    desc: "Pastoral access — members, attendance, reports.",
    icon: "auto_stories",
    gradient: "linear-gradient(135deg,#6A1B9A,#AB47BC)",
  },
  {
    value: "staff",
    label: "Staff",
    desc: "Day-to-day operations and member management.",
    icon: "support_agent",
    gradient: "linear-gradient(135deg,#1A73E8,#4285F4)",
  },
  {
    value: "treasurer",
    label: "Treasurer",
    desc: "Tithes, welfare, offerings, accounting reports.",
    icon: "account_balance",
    gradient: "linear-gradient(135deg,#2E7D32,#66BB6A)",
  },
  {
    value: "department_head",
    label: "Department Head",
    desc: "Manages one department: members + attendance.",
    icon: "groups",
    gradient: "linear-gradient(135deg,#0277BD,#4FC3F7)",
  },
  {
    value: "ministry_head",
    label: "Ministry Head",
    desc: "Leads one ministry: members + attendance.",
    icon: "diversity_3",
    gradient: "linear-gradient(135deg,#7B1FA2,#BA68C8)",
  },
  {
    value: "branch_admin",
    label: "Branch Admin",
    desc: "Manages one branch only — members, payments, attendance for that branch.",
    icon: "store",
    gradient: "linear-gradient(135deg,#00695C,#26A69A)",
  },
  {
    value: "member",
    label: "Member",
    desc: "Personal dashboard — tithes, attendance, profile.",
    icon: "person",
    gradient: "linear-gradient(135deg,#546E7A,#90A4AE)",
  },
];

const roleOf = (value) => ROLE_CATALOGUE.find((r) => r.value === value) || ROLE_CATALOGUE[6];

// Module catalogue — must match the keys in config/modules.php on the backend.
// Lets an admin grant a user access to a specific module on top of their role.
const MODULE_CATALOGUE = [
  { value: "members", label: "Members" },
  { value: "departments", label: "Departments" },
  { value: "ministries", label: "Ministries" },
  { value: "attendance", label: "Attendance" },
  { value: "biometric", label: "Biometric" },
  { value: "payments", label: "Payments / Accounts" },
  { value: "sms", label: "SMS Messaging" },
  { value: "reports", label: "Reports" },
  { value: "calendar", label: "Calendar" },
  { value: "visitors", label: "Visitors" },
  { value: "new_converts", label: "New Converts" },
  { value: "small_groups", label: "Small Groups" },
  { value: "pastoral_care", label: "Pastoral Care" },
  { value: "serving_teams", label: "Serving Teams" },
  { value: "qr_codes", label: "QR Codes / Check-In" },
  { value: "child_checkin", label: "Child Check-In" },
  { value: "bulk_email", label: "Bulk Email" },
  { value: "sacramental_records", label: "Sacramental Records" },
  { value: "branches", label: "Branches" },
  { value: "audit_logs", label: "Audit Log" },
  { value: "users", label: "Users Account" },
  { value: "projects", label: "Projects" },
  { value: "expenses", label: "Expenses" },
  { value: "payroll", label: "Payroll" },
];

function User() {
  const {
    columns,
    rows,
    isLoading,
    error,
    members,
    isLoadingMembers,
    handleMemberSelect,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedUser,
    setSelectedUser,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddUser,
  } = useUserData();

  // Safe access to members data
  const safeMembers = members || [];

  // Departments + Ministries — fetched once for the "Group to lead" picker
  // that appears when role is department_head / ministry_head.
  const { church } = useAuth();
  const [departments, setDepartments] = useState([]);
  const [ministries, setMinistries] = useState([]);
  const [branches, setBranches] = useState([]);
  const [memberSearch, setMemberSearch] = useState("");
  const memberSearchRef = useRef(null);

  useEffect(() => {
    if (!church?.id) return;
    const token = localStorage.getItem("authToken");
    const headers = { Accept: "application/json", Authorization: `Bearer ${token}` };
    fetch(`${API_BASE}/departments/church/${church.id}`, { headers })
      .then((r) => r.json())
      .then((j) => setDepartments(Array.isArray(j) ? j : j?.data || []))
      .catch(() => setDepartments([]));
    fetch(`${API_BASE}/ministries/church/${church.id}`, { headers })
      .then((r) => r.json())
      .then((j) => setMinistries(Array.isArray(j) ? j : j?.data || []))
      .catch(() => setMinistries([]));
    fetch(`${API_BASE}/branches/church/${church.id}`, { headers })
      .then((r) => r.json())
      .then((j) => setBranches(Array.isArray(j) ? j : j?.data || []))
      .catch(() => setBranches([]));
  }, [church?.id]);

  // Pre-fill the picker when editing an existing head user
  useEffect(() => {
    if (!selectedUser?.id) return;
    if (selectedUser.role === "department_head" && !selectedUser.department_id) {
      const dept = departments.find(
        (d) => Number(d.head_member_id) === Number(selectedUser.member_id)
      );
      if (dept) setSelectedUser((prev) => ({ ...prev, department_id: dept.id }));
    }
    if (selectedUser.role === "ministry_head" && !selectedUser.ministry_id) {
      const min = ministries.find(
        (m) => Number(m.head_member_id) === Number(selectedUser.member_id)
      );
      if (min) setSelectedUser((prev) => ({ ...prev, ministry_id: min.id }));
    }
  }, [selectedUser?.id, selectedUser?.role, departments, ministries, selectedUser?.member_id]);

  if (isLoading) {
    return <Typography sx={{ py: 6 }}>Loading users...</Typography>;
  }

  if (error) {
    return (
      <Typography color="error" sx={{ py: 6 }}>
        Error: {error}
      </Typography>
    );
  }

  return (
    <Box>
      <PageHeader
        title="User Accounts"
        subtitle="Manage church member accounts"
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddUser}>
            New User
          </Button>
        }
      />

      <Card>
        <DataTable
          table={{ columns, rows }}
          isSorted={false}
          entriesPerPage
          showTotalEntries
          noEndBorder
          canSearch
          searchLabel="Search users..."
        />
      </Card>

      {/* Edit User Modal */}
      <FormDialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title={selectedUser?.id ? "Edit User" : "Create New User"}
        onSave={handleEditSubmit}
        saveLabel={selectedUser?.id ? "Save Changes" : "Create User"}
        maxWidth="md"
      >
        <FormSection>
          <FormControl
            fullWidth
            required={["member", "department_head", "ministry_head"].includes(selectedUser?.role)}
          >
            <InputLabel id="member-select-label">Select Member</InputLabel>
            <Select
              labelId="member-select-label"
              value={selectedUser?.member_id ?? ""}
              onChange={(e) => {
                const selectedMemberId = e.target.value;
                const selectedMember = safeMembers.find(
                  (member) => Number(member.id) === Number(selectedMemberId)
                );
                if (selectedMember) handleMemberSelect(selectedMember);
              }}
              renderValue={(value) => {
                const m = safeMembers.find((x) => Number(x.id) === Number(value));
                if (!m) return null;
                return (
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Avatar
                      src={m.photo ? `${API_HOST}/storage/${m.photo}` : undefined}
                      sx={{ width: 28, height: 28, bgcolor: "#1A73E8", fontSize: 14 }}
                    >
                      {(m.first_name?.[0] || "?").toUpperCase()}
                    </Avatar>
                    <Box display="flex" flexDirection="column" lineHeight={1.2}>
                      <span style={{ fontWeight: 600 }}>
                        {m.first_name} {m.surname}
                      </span>
                      <span style={{ fontSize: 11, color: "#666" }}>
                        {m.memberID || m.phone || m.email}
                      </span>
                    </Box>
                  </Box>
                );
              }}
              MenuProps={{
                PaperProps: { style: { maxHeight: 380 } },
              }}
              onOpen={() => setTimeout(() => memberSearchRef.current?.focus(), 50)}
              onClose={() => setMemberSearch("")}
            >
              <ListSubheader sx={{ pt: 1, pb: 0.5, bgcolor: "background.paper" }}>
                <TextField
                  inputRef={memberSearchRef}
                  size="small"
                  fullWidth
                  placeholder="Search members…"
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== "Escape") e.stopPropagation();
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </ListSubheader>
              {isLoadingMembers ? (
                <MenuItem disabled>
                  <Box display="flex" justifyContent="center" width="100%">
                    <CircularProgress size={20} />
                  </Box>
                </MenuItem>
              ) : safeMembers.length > 0 ? (
                safeMembers
                  .filter((m) => {
                    if (!memberSearch) return true;
                    const q = memberSearch.toLowerCase();
                    return (
                      (m.first_name || "").toLowerCase().includes(q) ||
                      (m.surname || "").toLowerCase().includes(q) ||
                      (m.memberID || "").toLowerCase().includes(q) ||
                      (m.phone || "").includes(memberSearch)
                    );
                  })
                  .map((m) => (
                    <MenuItem key={m.id} value={m.id} sx={{ py: 1.25 }}>
                      <ListItemAvatar sx={{ minWidth: 44 }}>
                        <Avatar
                          src={m.photo ? `${API_HOST}/storage/${m.photo}` : undefined}
                          sx={{ width: 36, height: 36, bgcolor: "#1A73E8", fontSize: 16 }}
                        >
                          {(m.first_name?.[0] || "?").toUpperCase()}
                          {(m.surname?.[0] || "").toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <span style={{ fontWeight: 600 }}>
                            {m.first_name} {m.surname}
                          </span>
                        }
                        secondary={
                          <Box
                            component="span"
                            sx={{
                              display: "flex",
                              gap: 1,
                              fontSize: 11,
                              color: "text.secondary",
                            }}
                          >
                            {m.memberID && <span>ID: {m.memberID}</span>}
                            {m.phone && <span>· {m.phone}</span>}
                            {m.email && <span>· {m.email}</span>}
                          </Box>
                        }
                      />
                    </MenuItem>
                  ))
              ) : (
                <MenuItem disabled>No members found</MenuItem>
              )}
            </Select>
          </FormControl>
          {!["member", "department_head", "ministry_head"].includes(selectedUser?.role) && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
              Optional for this role — only Member, Department Head, and Ministry Head accounts need
              to be linked to a congregation member.
            </Typography>
          )}

          {/* Display selected member info */}
          {selectedUser?.member_id && (
            <Box p={2} mt={2} borderRadius={1} bgcolor="grey.100">
              <Typography variant="button" fontWeight="medium">
                Selected Member: {selectedUser.member_name}
              </Typography>
              {selectedUser.email && (
                <Typography variant="caption" display="block">
                  Email: {selectedUser.email}
                </Typography>
              )}
              {selectedUser.phone && (
                <Typography variant="caption" display="block">
                  Phone: {selectedUser.phone}
                </Typography>
              )}
            </Box>
          )}
        </FormSection>

        <FormSection>
          <Grid container spacing={3}>
            {/* Username */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Username"
                value={selectedUser?.username || ""}
                onChange={(e) =>
                  setSelectedUser({
                    ...selectedUser,
                    username: e.target.value,
                  })
                }
                fullWidth
                required
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Email"
                type="email"
                value={selectedUser?.email || ""}
                onChange={(e) =>
                  setSelectedUser({
                    ...selectedUser,
                    email: e.target.value,
                  })
                }
                fullWidth
                required
              />
            </Grid>

            {/* Phone */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Phone"
                value={selectedUser?.phone || ""}
                onChange={(e) =>
                  setSelectedUser({
                    ...selectedUser,
                    phone: e.target.value,
                  })
                }
                fullWidth
              />
            </Grid>

            {/* Role — rich select with icon + description */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel id="role-select-label">Role</InputLabel>
                <Select
                  labelId="role-select-label"
                  value={selectedUser?.role || "member"}
                  label="Role"
                  onChange={(e) => setSelectedUser({ ...selectedUser, role: e.target.value })}
                  renderValue={(value) => {
                    const r = roleOf(value);
                    return (
                      <Box display="flex" alignItems="center" gap={1.25}>
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: 1,
                            background: r.gradient,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Icon sx={{ color: "white", fontSize: 16 }}>{r.icon}</Icon>
                        </Box>
                        <span style={{ fontWeight: 600 }}>{r.label}</span>
                      </Box>
                    );
                  }}
                  MenuProps={{ PaperProps: { style: { maxHeight: 380 } } }}
                >
                  {ROLE_CATALOGUE.map((r) => (
                    <MenuItem key={r.value} value={r.value} sx={{ py: 1.25 }}>
                      <Box display="flex" alignItems="center" gap={1.5} width="100%">
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1,
                            background: r.gradient,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <Icon sx={{ color: "white", fontSize: 18 }}>{r.icon}</Icon>
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Box sx={{ fontWeight: 600, fontSize: 14 }}>{r.label}</Box>
                          <Box sx={{ fontSize: 12, color: "text.secondary" }}>{r.desc}</Box>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Status — colored dot */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="status-select-label">Status</InputLabel>
                <Select
                  labelId="status-select-label"
                  value={selectedUser?.status || "active"}
                  label="Status"
                  onChange={(e) => setSelectedUser({ ...selectedUser, status: e.target.value })}
                  renderValue={(value) => (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          bgcolor: value === "active" ? "success.main" : "error.main",
                          boxShadow:
                            value === "active"
                              ? "0 0 0 3px rgba(46,125,50,.18)"
                              : "0 0 0 3px rgba(211,47,47,.18)",
                        }}
                      />
                      <span style={{ textTransform: "capitalize", fontWeight: 600 }}>{value}</span>
                    </Box>
                  )}
                >
                  {[
                    { v: "active", color: "success.main", icon: "check_circle" },
                    { v: "inactive", color: "error.main", icon: "cancel" },
                  ].map((o) => (
                    <MenuItem key={o.v} value={o.v}>
                      <Box display="flex" alignItems="center" gap={1.25}>
                        <Icon sx={{ color: o.color, fontSize: 20 }}>{o.icon}</Icon>
                        <span style={{ textTransform: "capitalize", fontWeight: 600 }}>{o.v}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </FormSection>

        {/* "Group to lead" — only when role is a head role */}
        {(selectedUser?.role === "department_head" || selectedUser?.role === "ministry_head") && (
          <FormSection>
            {selectedUser?.role === "department_head" && (
              <Box>
                <FormControl fullWidth required>
                  <InputLabel id="dept-lead-label">Department to lead</InputLabel>
                  <Select
                    labelId="dept-lead-label"
                    value={selectedUser?.department_id || ""}
                    label="Department to lead"
                    onChange={(e) =>
                      setSelectedUser({
                        ...selectedUser,
                        department_id: e.target.value,
                        ministry_id: null,
                      })
                    }
                    startAdornment={
                      <InputAdornment position="start">
                        <Icon sx={{ color: "info.main" }}>groups</Icon>
                      </InputAdornment>
                    }
                    renderValue={(value) => {
                      const d = departments.find((x) => x.id === value);
                      if (!d) return <span style={{ color: "#999" }}>Choose a department…</span>;
                      return (
                        <Box display="flex" alignItems="center" gap={1.25}>
                          <Box
                            sx={{
                              width: 26,
                              height: 26,
                              borderRadius: 1,
                              background: "linear-gradient(135deg,#1A73E8,#4285F4)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Icon sx={{ color: "white", fontSize: 16 }}>groups</Icon>
                          </Box>
                          <span style={{ fontWeight: 600 }}>{d.name}</span>
                        </Box>
                      );
                    }}
                  >
                    {departments.length === 0 && <MenuItem disabled>No departments yet</MenuItem>}
                    {departments.map((d) => {
                      const alreadyTaken =
                        d.head_member_id &&
                        Number(d.head_member_id) !== Number(selectedUser?.member_id);
                      return (
                        <MenuItem key={d.id} value={d.id} sx={{ py: 1.25 }}>
                          <Box display="flex" alignItems="center" gap={1.25} width="100%">
                            <Box
                              sx={{
                                width: 30,
                                height: 30,
                                borderRadius: 1,
                                background: "linear-gradient(135deg,#1A73E8,#4285F4)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <Icon sx={{ color: "white", fontSize: 18 }}>groups</Icon>
                            </Box>
                            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                              <Box sx={{ fontWeight: 600, fontSize: 14 }}>{d.name}</Box>
                              {alreadyTaken && (
                                <Box sx={{ fontSize: 12, color: "warning.dark" }}>
                                  Currently has another head — will be replaced
                                </Box>
                              )}
                            </Box>
                            {alreadyTaken && (
                              <Chip
                                size="small"
                                color="warning"
                                label="Taken"
                                sx={{ fontWeight: 600 }}
                              />
                            )}
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
                  This person will become the head of the selected department on save.
                </Typography>
              </Box>
            )}

            {selectedUser?.role === "ministry_head" && (
              <Box>
                <FormControl fullWidth required>
                  <InputLabel id="min-lead-label">Ministry to lead</InputLabel>
                  <Select
                    labelId="min-lead-label"
                    value={selectedUser?.ministry_id || ""}
                    label="Ministry to lead"
                    onChange={(e) =>
                      setSelectedUser({
                        ...selectedUser,
                        ministry_id: e.target.value,
                        department_id: null,
                      })
                    }
                    renderValue={(value) => {
                      const m = ministries.find((x) => x.id === value);
                      if (!m) return <span style={{ color: "#999" }}>Choose a ministry…</span>;
                      return (
                        <Box display="flex" alignItems="center" gap={1.25}>
                          <Box
                            sx={{
                              width: 26,
                              height: 26,
                              borderRadius: 1,
                              background: "linear-gradient(135deg,#7B1FA2,#BA68C8)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Icon sx={{ color: "white", fontSize: 16 }}>diversity_3</Icon>
                          </Box>
                          <span style={{ fontWeight: 600 }}>{m.name}</span>
                        </Box>
                      );
                    }}
                  >
                    {ministries.length === 0 && <MenuItem disabled>No ministries yet</MenuItem>}
                    {ministries.map((m) => {
                      const alreadyTaken =
                        m.head_member_id &&
                        Number(m.head_member_id) !== Number(selectedUser?.member_id);
                      return (
                        <MenuItem key={m.id} value={m.id} sx={{ py: 1.25 }}>
                          <Box display="flex" alignItems="center" gap={1.25} width="100%">
                            <Box
                              sx={{
                                width: 30,
                                height: 30,
                                borderRadius: 1,
                                background: "linear-gradient(135deg,#7B1FA2,#BA68C8)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <Icon sx={{ color: "white", fontSize: 18 }}>diversity_3</Icon>
                            </Box>
                            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                              <Box sx={{ fontWeight: 600, fontSize: 14 }}>{m.name}</Box>
                              {alreadyTaken && (
                                <Box sx={{ fontSize: 12, color: "warning.dark" }}>
                                  Currently has another head — will be replaced
                                </Box>
                              )}
                            </Box>
                            {alreadyTaken && (
                              <Chip
                                size="small"
                                color="warning"
                                label="Taken"
                                sx={{ fontWeight: 600 }}
                              />
                            )}
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
                  This person will become the head of the selected ministry on save.
                </Typography>
              </Box>
            )}
          </FormSection>
        )}

        {/* "Branch to manage" — only when role is branch_admin */}
        {selectedUser?.role === "branch_admin" && (
          <FormSection>
            <FormControl fullWidth required>
              <InputLabel id="branch-admin-label">Branch to manage</InputLabel>
              <Select
                labelId="branch-admin-label"
                value={selectedUser?.branch_id || ""}
                label="Branch to manage"
                onChange={(e) => setSelectedUser({ ...selectedUser, branch_id: e.target.value })}
              >
                {branches.length === 0 && <MenuItem disabled>No branches yet</MenuItem>}
                {branches.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
              This user will only see members, payments, and attendance for this branch.
            </Typography>
          </FormSection>
        )}

        {/* Password fields — required for new users, optional reset for existing */}
        <FormSection>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label={selectedUser?.id ? "New Password (leave blank to keep)" : "Password"}
                type="password"
                value={selectedUser?.password || ""}
                onChange={(e) =>
                  setSelectedUser({
                    ...selectedUser,
                    password: e.target.value,
                  })
                }
                fullWidth
                required={!selectedUser?.id}
                helperText={selectedUser?.id ? "Only fill if you want to change the password" : ""}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={selectedUser?.id ? "Confirm New Password" : "Confirm Password"}
                type="password"
                value={selectedUser?.confirmPassword || ""}
                onChange={(e) =>
                  setSelectedUser({
                    ...selectedUser,
                    confirmPassword: e.target.value,
                  })
                }
                fullWidth
                required={!selectedUser?.id}
              />
            </Grid>
          </Grid>
        </FormSection>

        {/* Module access — additive grants on top of whatever the role above already allows */}
        <FormSection>
          <Typography variant="button" fontWeight="bold" display="block" mb={0.5}>
            Module Access
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
            Grant this user access to specific modules in addition to what their role already allows
            — e.g. let a Member into Pastoral Care or Child Check-In without changing their role.
          </Typography>
          <Grid container spacing={0}>
            {MODULE_CATALOGUE.map((m) => {
              const checked = (selectedUser?.permissions || []).includes(m.value);
              return (
                <Grid item xs={12} sm={6} md={4} key={m.value}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={checked}
                        onChange={(e) => {
                          const current = selectedUser?.permissions || [];
                          const next = e.target.checked
                            ? [...current, m.value]
                            : current.filter((v) => v !== m.value);
                          setSelectedUser({ ...selectedUser, permissions: next });
                        }}
                      />
                    }
                    label={<Typography variant="caption">{m.label}</Typography>}
                  />
                </Grid>
              );
            })}
          </Grid>
        </FormSection>
      </FormDialog>

      {/* Delete User Modal */}
      <ConfirmDialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Confirm Delete"
        message={`Are you sure you want to delete user \`${selectedUser?.username}\`?`}
        confirmLabel="Confirm Delete"
      />
    </Box>
  );
}

User.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object),
  rows: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  editModalOpen: PropTypes.bool,
  setEditModalOpen: PropTypes.func,
  deleteModalOpen: PropTypes.bool,
  setDeleteModalOpen: PropTypes.func,
  selectedUser: PropTypes.object,
  setSelectedUser: PropTypes.func,
  handleEditSubmit: PropTypes.func,
  handleDeleteConfirm: PropTypes.func,
  handleAddUser: PropTypes.func,
};

export default User;
